/**
 * AEO Impact Dashboard
 *
 * Measures whether AEO-enriched product pages produce measurable impact
 * on organic traffic, search rankings, engagement, ad performance,
 * and conversions — compared to non-AEO product pages.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  TrendingUp,
  TrendingDown,
  Eye,
  MousePointer,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  BarChart3,
  Users,
  DollarSign,
  Target,
  CheckCircle,
  Clock,
  AlertCircle,
  Package,
  Rocket,
  Gauge,
  Bot,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { useToast } from '@/components/ui/use-toast';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
} from 'recharts';
import {
  aeoImpactService,
  type AEOImpactData,
  type AdsComparison,
  type AdsCompleteness,
  type GSCBucketRow,
  type GA4BucketRow,
} from '@/services/aeoImpactService';
import { authFetch } from '@/utils/auth-fetch';
import { useImpactAvailability } from '@/hooks/useImpactAvailability';
import ImpactProductTab from '@/components/impact/ImpactProductTab';
import { InformeDiarioTab } from '@/components/impact/InformeDiarioTab';
import { ImpactDidTab, type DidData } from '@/components/impact/ImpactDidTab';
import {
  impactDashboardService,
  ImpactDashboardError,
  type ImpactDashboardData,
} from '@/services/impactDashboardService';

// =========================================================================
// Constants
// =========================================================================

const DATE_RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

// Earliest date covered by impact_serving (product_daily starts here; all "since January" queries use this).
export const IMPACT_DATA_START = '2026-01-01';

// Compute dateFrom for the product tab's preset options.
// "Last N days" = N calendar dates ending yesterday (data_until is always yesterday).
// custom with no from = '' (empty) — ImpactProductTab shows a prompt, no fetch.
export function resolveProductDateFrom(preset: string, customFrom: string): string {
  if (preset === 'ytd') return IMPACT_DATA_START;
  if (preset === 'custom') return customFrom;
  return new Date(Date.now() - parseInt(preset) * 86400000).toISOString().split('T')[0];
}

// Tabs served by the heavy getImpactData call (GET /api/aeo-impact/:siteId). That call scans raw
// Google Ads landing-page rows and times out (nginx 504) on large stores, so it is made ONLY when
// one of these tabs is open — never on page open (task-0bsfrrqqfa). The Ads tab is NOT one of
// them: it is served only by its own request (getAdsComparison), so it neither waits on nor
// starts this one.
export const LEGACY_TABS = ['organic', 'engagement', 'pipeline'];

const PIE_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#6b7280'];

// =========================================================================
// Helpers
// =========================================================================

const formatNumber = (n: number) => n?.toLocaleString() ?? '0';
const formatPercent = (n: number) => `${(n * 100).toFixed(1)}%`;
const formatPercentRaw = (n: number) => `${n.toFixed(1)}%`;
const formatCurrency = (n: number) =>
  `$${n?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}`;
const formatPosition = (n: number | null | undefined) =>
  n === null || n === undefined || Number.isNaN(n) ? '-' : n.toFixed(1);
const formatSeconds = (n: number) => {
  if (!n) return '0s';
  if (n < 60) return `${Math.round(n)}s`;
  return `${Math.floor(n / 60)}m ${Math.round(n % 60)}s`;
};

function ChangeIndicator({ value, inverted = false }: { value: number | null | undefined; inverted?: boolean }) {
  if (value === null || value === undefined) return <Minus className="h-4 w-4 text-muted-foreground" />;
  const isPositive = inverted ? value < 0 : value > 0;
  const Icon = isPositive ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  const color = isPositive ? 'text-green-500' : value < 0 ? 'text-red-500' : 'text-muted-foreground';
  return (
    <span className={`flex items-center ${color}`}>
      <Icon className="h-4 w-4" />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function MetricCard({
  title,
  value,
  change,
  icon: Icon,
  inverted = false,
}: {
  title: string;
  value: string;
  change?: number | null;
  icon: React.ElementType;
  inverted?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Icon className="h-5 w-5 text-muted-foreground" />
            {change !== undefined && <ChangeIndicator value={change} inverted={inverted} />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ComparisonCard({
  title,
  aeoValue,
  nonAeoValue,
  format = 'number',
  inverted = false,
}: {
  title: string;
  aeoValue: number;
  nonAeoValue: number;
  format?: 'number' | 'percent' | 'currency' | 'position' | 'seconds';
  inverted?: boolean;
}) {
  const fmt = (v: number) => {
    switch (format) {
      case 'percent': return formatPercent(v);
      case 'currency': return formatCurrency(v);
      case 'position': return formatPosition(v);
      case 'seconds': return formatSeconds(v);
      default: return formatNumber(v);
    }
  };

  const diff = nonAeoValue !== 0
    ? ((aeoValue - nonAeoValue) / Math.abs(nonAeoValue)) * 100
    : aeoValue > 0 ? 100 : 0;
  const isPositive = inverted ? diff < 0 : diff > 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">AEO Pages</p>
            <p className="text-xl font-bold text-blue-600">{fmt(aeoValue)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Non-AEO</p>
            <p className="text-xl font-bold">{fmt(nonAeoValue)}</p>
          </div>
        </div>
        {diff !== 0 && (
          <div className={`mt-2 text-sm flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
            {Math.abs(diff).toFixed(1)}% {isPositive ? 'better' : 'worse'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// "3 not refreshed yet, 1 missing, ..." — every non-ok state that is present, never hidden.
function adsCompletenessText(c: AdsCompleteness): string {
  const parts: string[] = [];
  if (c.pending) parts.push(`${c.pending} not refreshed yet`);
  if (c.stale) parts.push(`${c.stale} with newer data waiting`);
  if (c.failed) parts.push(`${c.failed} failed to refresh`);
  if (c.missing) parts.push(`${c.missing} not covered by any Ads sync`);
  if (c.expired) parts.push(`${c.expired} older than the Ads data retention`);
  if (c.sourceIncomplete) parts.push(`${c.sourceIncomplete} whose Ads sync has not completed successfully`);
  return parts.join(', ') || 'all included';
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}><CardContent className="pt-6"><Skeleton className="h-16 w-full" /></CardContent></Card>
        ))}
      </div>
      <Skeleton className="h-[300px] w-full" />
    </div>
  );
}

// =========================================================================
// Main Dashboard
// =========================================================================

export default function AEOImpactDashboard() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const siteId = selectedSite?.id || selectedSite?._id;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AEOImpactData | null>(null);
  const [legacyError, setLegacyError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState('30');
  const [activeTab, setActiveTab] = useState('organic');
  // Once the user picks a tab, the availability default below never overrides it.
  const [userPickedTab, setUserPickedTab] = useState(false);
  // Version counter for the legacy fetch — a response for an old site/range is ignored.
  const legacyVersion = useRef(0);
  // `${siteId}|${dateRange}` the current `data` belongs to, so moving between tabs does not refetch.
  const legacyLoadedFor = useRef<string | null>(null);
  // `${siteId}|${dateRange}` of the request in flight, so reopening a tab mid-request reuses it.
  const legacyPendingFor = useRef<string | null>(null);

  // Ads tab: its own request (GET /api/aeo-impact/:siteId/ads), far slower than the rest on large
  // stores, made only while the Ads tab is open. Same in-flight/cached/version rules as above.
  const [adsData, setAdsData] = useState<AdsComparison | null>(null);
  const [adsError, setAdsError] = useState<string | null>(null);
  const [adsLoading, setAdsLoading] = useState(false);
  const adsVersion = useRef(0);
  const adsLoadedFor = useRef<string | null>(null);
  const adsPendingFor = useRef<string | null>(null);

  // Por producto / 3A tab availability — null = loading, true = show tabs, false = hide tabs.
  // On call failure, error is set and the tabs show the real error (never silently hidden).
  const { available: productAvailable, error: availabilityError } = useImpactAvailability(siteId);
  const legacyActive = LEGACY_TABS.includes(activeTab);
  // A legacy tab may load only once it is a real choice: the user picked it, or availability said
  // the 3A tabs are off (false or error). While availability is pending, or true with no pick yet,
  // the page is about to open on informe-diario, so the heavy call must not start.
  const legacyAllowed = userPickedTab || productAvailable === false || availabilityError !== null;

  const selectTab = useCallback((tab: string) => {
    setUserPickedTab(true);
    setActiveTab(tab);
  }, []);

  useEffect(() => {
    if (productAvailable === true && !userPickedTab) {
      // The page opens on the 3A tabs when the product is configured.
      setActiveTab('informe-diario');
    }
    if (productAvailable === false) {
      setActiveTab(prev =>
        ['por-producto', 'informe-diario', 'impact-did'].includes(prev) ? 'organic' : prev
      );
    }
  }, [productAvailable, userPickedTab]);

  // 3A attribution dashboards
  const [informeData, setInformeData] = useState<ImpactDashboardData | null>(null);
  const [informeError, setInformeError] = useState<string | null>(null);
  const [informeLoading, setInformeLoading] = useState(false);
  // Version counter — incremented on site change so stale responses are ignored
  const informeVersion = useRef(0);

  // DiD tab state
  const [didData, setDidData] = useState<DidData | null>(null);
  const [didError, setDidError] = useState<string | null>(null);
  const [didLoading, setDidLoading] = useState(false);
  const didVersion = useRef(0);

  // Lifted product-tab date state — lives in the page so it survives the page's
  // loading cycle (loading=true unmounts the tab; when it remounts it gets these props).
  const [productPreset, setProductPreset] = useState('30');
  const [productCustomFrom, setProductCustomFrom] = useState('');
  const [productCustomTo, setProductCustomTo] = useState('');
  // data_until is always yesterday; end presets there so "Last N days" = exactly N calendar dates.
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  const productDateFrom = resolveProductDateFrom(productPreset, productCustomFrom);
  const effectiveProductDateTo = (productPreset === 'custom' && productCustomTo) ? productCustomTo : yesterday;

  const fetchData = useCallback(async () => {
    if (!siteId) return;
    const key = `${siteId}|${dateRange}`;
    const v = ++legacyVersion.current;
    legacyPendingFor.current = key;
    setLoading(true);
    setLegacyError(null);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(dateRange) * 86400000).toISOString().split('T')[0];
      const result = await aeoImpactService.getImpactData(siteId, { startDate, endDate });
      if (legacyVersion.current !== v) return; // stale — site or range changed mid-flight
      setData(result);
      legacyLoadedFor.current = key;
    } catch (error: any) {
      if (legacyVersion.current !== v) return;
      setData(null); // never show another site's or range's numbers under a failed refresh
      legacyLoadedFor.current = null; // reopening a legacy tab retries
      setLegacyError(error.message);
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      if (legacyVersion.current === v) {
        legacyPendingFor.current = null;
        setLoading(false);
      }
    }
  }, [siteId, dateRange, toast]);

  const fetchAdsData = useCallback(async () => {
    if (!siteId) return;
    const key = `${siteId}|${dateRange}`;
    const v = ++adsVersion.current;
    adsPendingFor.current = key;
    setAdsLoading(true);
    setAdsError(null);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(dateRange) * 86400000).toISOString().split('T')[0];
      const result = await aeoImpactService.getAdsComparison(siteId, { startDate, endDate });
      if (adsVersion.current !== v) return; // stale — site or range changed mid-flight
      setAdsData(result);
      adsLoadedFor.current = key;
    } catch (error: any) {
      if (adsVersion.current !== v) return;
      setAdsData(null); // never show another site's or range's numbers under a failed refresh
      adsLoadedFor.current = null; // reopening the Ads tab retries
      setAdsError(error.message);
    } finally {
      if (adsVersion.current === v) {
        adsPendingFor.current = null;
        setAdsLoading(false);
      }
    }
  }, [siteId, dateRange]);

  const fetchInformeData = useCallback(async () => {
    if (!siteId) return;
    const v = ++informeVersion.current;
    setInformeLoading(true);
    setInformeError(null);
    try {
      const result = await impactDashboardService.get(siteId, 'impact');
      if (informeVersion.current !== v) return; // stale — site changed mid-flight
      setInformeData(result as ImpactDashboardData);
    } catch (err) {
      if (informeVersion.current !== v) return;
      setInformeData(null); // clear stale data — never show unlabelled old snapshot on failed refresh
      setInformeError(err instanceof ImpactDashboardError ? err.message : 'Error loading Impact and attribution');
    } finally {
      if (informeVersion.current === v) setInformeLoading(false);
    }
  }, [siteId]);

  const fetchDidData = useCallback(async () => {
    if (!siteId) return;
    const v = ++didVersion.current;
    setDidLoading(true);
    setDidError(null);
    try {
      const res = await authFetch(`/api/impact-dashboard/${siteId}/did`);
      if (didVersion.current !== v) return;
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        if (didVersion.current !== v) return;
        setDidData(null);
        setDidError(body.error ?? `DiD data unavailable (${res.status})`);
        return;
      }
      const body = await res.json() as DidData;
      if (didVersion.current !== v) return;
      setDidData(body);
    } catch (err) {
      if (didVersion.current !== v) return;
      setDidData(null);
      setDidError(err instanceof Error ? err.message : 'Error loading DiD data');
    } finally {
      if (didVersion.current === v) setDidLoading(false);
    }
  }, [siteId]);

  // Clear dashboard state whenever the site changes so stale data is not shown
  useEffect(() => {
    legacyVersion.current += 1;
    legacyLoadedFor.current = null;
    legacyPendingFor.current = null;
    setData(null);
    setLegacyError(null);
    setLoading(false);
    adsVersion.current += 1;
    adsLoadedFor.current = null;
    adsPendingFor.current = null;
    setAdsData(null);
    setAdsError(null);
    setAdsLoading(false);
    informeVersion.current += 1;
    setInformeData(null);
    setInformeError(null);
    setInformeLoading(false);
    didVersion.current += 1;
    setDidData(null);
    setDidError(null);
    setDidLoading(false);
  }, [siteId]);

  // Declared AFTER the site-change reset so, on a site switch, the reset runs first.
  // Lazy: the heavy call runs only while a legacy tab is open, and only once per site + range.
  useEffect(() => {
    if (!legacyActive || !legacyAllowed) return;
    const key = `${siteId}|${dateRange}`;
    if (legacyPendingFor.current === key) return; // the same request is already in flight: reuse it
    if (legacyLoadedFor.current === key) {
      // Back on the range `data` already holds. A request still in flight for another range must
      // not land on top of it, so invalidate it (codex review: 30 → 7 → 30 showed 7-day numbers).
      if (legacyPendingFor.current !== null) {
        legacyVersion.current += 1;
        legacyPendingFor.current = null;
        setLoading(false);
      }
      return;
    }
    fetchData();
    // activeTab is a dependency so moving between legacy tabs retries a failed request
    // (the pending/cached guards above keep a success or an in-flight request from refetching).
  }, [activeTab, legacyActive, legacyAllowed, fetchData, siteId, dateRange]);

  // Same gate as the legacy fetch: Ads is a real choice only once picked or once the 3A tabs are off.
  useEffect(() => {
    if (activeTab !== 'ads' || !legacyAllowed) return;
    const key = `${siteId}|${dateRange}`;
    if (adsPendingFor.current === key) return;
    if (adsLoadedFor.current === key) {
      if (adsPendingFor.current !== null) {
        adsVersion.current += 1;
        adsPendingFor.current = null;
        setAdsLoading(false);
      }
      return;
    }
    fetchAdsData();
  }, [activeTab, legacyAllowed, fetchAdsData, siteId, dateRange]);

  // Fetch 3A snapshot when the informe tab becomes active (keyboard/programmatic activation)
  // or when the site changes while the tab is already active.
  // fetchInformeData is stable per siteId; the version ref guards staleness.
  useEffect(() => {
    if (activeTab === 'informe-diario') fetchInformeData();
  }, [activeTab, fetchInformeData]);

  useEffect(() => {
    if (activeTab === 'impact-did') fetchDidData();
  }, [activeTab, fetchDidData]);

  if (!siteId) {
    return (
      <div className="p-6">
        <Alert><AlertDescription>Select a site to view AEO impact data.</AlertDescription></Alert>
      </div>
    );
  }

  const aeo = (key: string) => data?.gscComparison?.aggregate?.aeo?.[key as keyof typeof data.gscComparison.aggregate.aeo] ?? 0;
  const nonAeo = (key: string) => data?.gscComparison?.aggregate?.non_aeo?.[key as keyof typeof data.gscComparison.aggregate.non_aeo] ?? 0;
  // Position must NOT go through the `?? 0` helpers above. Zero is a legal-looking
  // value for a count and an impossible one for a position (ranks start at 1), so a
  // missing position rendered as 0.0 reads as the best possible rank. Read it raw and
  // let formatPosition show '-'.
  const aeoPosition = data?.gscComparison?.aggregate?.aeo?.avgPosition ?? null;
  const nonAeoPosition = data?.gscComparison?.aggregate?.non_aeo?.avgPosition ?? null;
  const ga4Aeo = data?.ga4Comparison?.aggregate?.aeo;
  const ga4NonAeo = data?.ga4Comparison?.aggregate?.non_aeo;
  const adsAeo = adsData?.aggregate?.aeo;
  const adsNonAeo = adsData?.aggregate?.non_aeo;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AEO Impact Dashboard</h1>
          <p className="text-muted-foreground">Compare AEO-enriched vs non-AEO product page performance</p>
        </div>
        {/* Hide date selector for tabs that show pre-computed snapshots (their own date range) */}
        {!['por-producto', 'informe-diario', 'impact-did'].includes(activeTab) && (
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGES.map(r => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Legacy loading/error is scoped to the legacy tabs: the tab bar and the 3A tabs never wait on it. */}
      {legacyActive && (loading || (!data && !legacyError && !legacyAllowed)) && <LoadingSkeleton />}
      {legacyActive && !loading && legacyError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{legacyError}</AlertDescription>
        </Alert>
      )}

        <>
          {/* Summary KPI Cards — only when comparison data is available */}
          {!loading && data && <div className="grid grid-cols-4 gap-4">
            <MetricCard
              title="Published Pages"
              value={formatNumber(data.summary.totalPublished)}
              icon={Package}
            />
            <MetricCard
              title="Deployed & Live"
              value={formatNumber(data.summary.totalDeployed)}
              icon={Rocket}
            />
            <MetricCard
              title="Deployment Rate"
              value={formatPercentRaw(data.summary.deploymentRate)}
              icon={CheckCircle}
            />
            <MetricCard
              title="Catalog Coverage"
              value={formatPercentRaw(data.summary.catalogCoverage)}
              icon={Gauge}
            />
          </div>}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={selectTab}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="organic">Organic & Search</TabsTrigger>
              <TabsTrigger value="engagement">Engagement</TabsTrigger>
              <TabsTrigger value="ads">Ads Performance</TabsTrigger>
              <TabsTrigger value="pipeline">Pipeline & AI</TabsTrigger>
              {(productAvailable === true || availabilityError !== null) && (
                <TabsTrigger value="por-producto">By product</TabsTrigger>
              )}
              {(productAvailable === true || availabilityError !== null) && (
                <TabsTrigger value="informe-diario">Impact and attribution</TabsTrigger>
              )}
              {(productAvailable === true || availabilityError !== null) && (
                <TabsTrigger value="impact-did">Impact: DiD (difference in differences)</TabsTrigger>
              )}
            </TabsList>

            {/* Existing comparison tabs — only rendered when comparison data is available */}
            {!loading && data && <>

            {/* Tab 1: Organic & Search */}
            <TabsContent value="organic" className="space-y-6">
              {/* Organic Impact Chart */}
              {data.organicImpact.daily.length > 0 ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Organic Traffic Impact</CardTitle>
                        <CardDescription>Total organic vs deployed-page organic traffic</CardDescription>
                      </div>
                      {data.organicImpact.trend !== null && (
                        <Badge variant={data.organicImpact.trend > 0 ? 'default' : 'destructive'}>
                          <ChangeIndicator value={data.organicImpact.trend} /> 7d trend
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={data.organicImpact.daily}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={(d: string) => d.slice(5)} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                        <Tooltip />
                        <Legend />
                        <Bar yAxisId="left" dataKey="totalOrganic" fill="#94a3b8" name="Total Organic" />
                        <Bar yAxisId="left" dataKey="deployedOrganic" fill="#3b82f6" name="Deployed Organic" />
                        <Line yAxisId="right" dataKey="pctOnEnriched" stroke="#f59e0b" name="% on Enriched" dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              ) : (
                <Alert><AlertDescription>No organic traffic data available for this period.</AlertDescription></Alert>
              )}

              {/* GSC Comparison Cards */}
              {data.gscComparison.aggregate.aeo ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">AEO Pages ({aeo('products')} products)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-muted-foreground">Impressions</span><p className="font-bold">{formatNumber(aeo('totalImpressions') as number)}</p></div>
                          <div><span className="text-muted-foreground">Clicks</span><p className="font-bold">{formatNumber(aeo('totalClicks') as number)}</p></div>
                          <div><span className="text-muted-foreground">CTR</span><p className="font-bold">{formatPercent(aeo('avgCtr') as number)}</p></div>
                          <div><span className="text-muted-foreground">Avg Position</span><p className="font-bold">{formatPosition(aeoPosition)}</p></div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Non-AEO Pages ({nonAeo('products')} products)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-muted-foreground">Impressions</span><p className="font-bold">{formatNumber(nonAeo('totalImpressions') as number)}</p></div>
                          <div><span className="text-muted-foreground">Clicks</span><p className="font-bold">{formatNumber(nonAeo('totalClicks') as number)}</p></div>
                          <div><span className="text-muted-foreground">CTR</span><p className="font-bold">{formatPercent(nonAeo('avgCtr') as number)}</p></div>
                          <div><span className="text-muted-foreground">Avg Position</span><p className="font-bold">{formatPosition(nonAeoPosition)}</p></div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Traffic Bucket Table */}
                  {data.gscComparison.buckets.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>GSC Performance by Traffic Bucket</CardTitle>
                        <CardDescription>Fair comparison within same impression tiers</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Bucket</TableHead>
                              <TableHead>Group</TableHead>
                              <TableHead className="text-right">Products</TableHead>
                              <TableHead className="text-right">Impressions</TableHead>
                              <TableHead className="text-right">Clicks</TableHead>
                              <TableHead className="text-right">CTR</TableHead>
                              <TableHead className="text-right">Avg Position</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.gscComparison.buckets.map((row: GSCBucketRow, i: number) => (
                              <TableRow key={i} className={row.group === 'aeo' ? 'bg-blue-50/50' : ''}>
                                <TableCell>{row.bucket}</TableCell>
                                <TableCell>
                                  <Badge variant={row.group === 'aeo' ? 'default' : 'secondary'}>
                                    {row.group === 'aeo' ? 'AEO' : 'Non-AEO'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">{formatNumber(row.products)}</TableCell>
                                <TableCell className="text-right">{formatNumber(row.totalImpressions)}</TableCell>
                                <TableCell className="text-right">{formatNumber(row.totalClicks)}</TableCell>
                                <TableCell className="text-right">{formatPercent(row.avgCtr)}</TableCell>
                                <TableCell className="text-right">{formatPosition(row.avgPosition)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Alert><AlertDescription>No GSC data available for product pages in this period.</AlertDescription></Alert>
              )}
            </TabsContent>

            {/* Tab 2: Engagement */}
            <TabsContent value="engagement" className="space-y-6">
              {ga4Aeo ? (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <ComparisonCard
                      title="Bounce Rate"
                      aeoValue={ga4Aeo.avgBounceRate}
                      nonAeoValue={ga4NonAeo?.avgBounceRate ?? 0}
                      format="percent"
                      inverted
                    />
                    <ComparisonCard
                      title="Engagement Rate"
                      aeoValue={ga4Aeo.avgEngagementRate}
                      nonAeoValue={ga4NonAeo?.avgEngagementRate ?? 0}
                      format="percent"
                    />
                    <ComparisonCard
                      title="Time on Page"
                      aeoValue={ga4Aeo.avgTimeOnPage}
                      nonAeoValue={ga4NonAeo?.avgTimeOnPage ?? 0}
                      format="seconds"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <ComparisonCard
                      title="Conversions"
                      aeoValue={ga4Aeo.totalConversions}
                      nonAeoValue={ga4NonAeo?.totalConversions ?? 0}
                    />
                    <ComparisonCard
                      title="Revenue per Session"
                      aeoValue={ga4Aeo.revenuePerSession}
                      nonAeoValue={ga4NonAeo?.revenuePerSession ?? 0}
                      format="currency"
                    />
                    <ComparisonCard
                      title="Total Sessions"
                      aeoValue={ga4Aeo.totalSessions}
                      nonAeoValue={ga4NonAeo?.totalSessions ?? 0}
                    />
                  </div>

                  {/* GA4 Bucket Table */}
                  {data.ga4Comparison.buckets.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>GA4 Engagement by Session Bucket</CardTitle>
                        <CardDescription>Fair comparison within same session volume tiers</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Bucket</TableHead>
                              <TableHead>Group</TableHead>
                              <TableHead className="text-right">Products</TableHead>
                              <TableHead className="text-right">Sessions</TableHead>
                              <TableHead className="text-right">Bounce Rate</TableHead>
                              <TableHead className="text-right">Engagement</TableHead>
                              <TableHead className="text-right">Avg Time</TableHead>
                              <TableHead className="text-right">Conversions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.ga4Comparison.buckets.map((row: GA4BucketRow, i: number) => (
                              <TableRow key={i} className={row.group === 'aeo' ? 'bg-blue-50/50' : ''}>
                                <TableCell>{row.bucket}</TableCell>
                                <TableCell>
                                  <Badge variant={row.group === 'aeo' ? 'default' : 'secondary'}>
                                    {row.group === 'aeo' ? 'AEO' : 'Non-AEO'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">{formatNumber(row.products)}</TableCell>
                                <TableCell className="text-right">{formatNumber(row.totalSessions)}</TableCell>
                                <TableCell className="text-right">{formatPercent(row.avgBounceRate)}</TableCell>
                                <TableCell className="text-right">{formatPercent(row.avgEngagementRate)}</TableCell>
                                <TableCell className="text-right">{formatSeconds(row.avgTimeOnPage)}</TableCell>
                                <TableCell className="text-right">{formatNumber(row.totalConversions)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Alert><AlertDescription>No GA4 engagement data available for product pages in this period.</AlertDescription></Alert>
              )}
            </TabsContent>

            {/* Tab 4: Pipeline & AI */}
            <TabsContent value="pipeline" className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Deployment Status Pie */}
                <Card>
                  <CardHeader>
                    <CardTitle>Deployment Status</CardTitle>
                    <CardDescription>Distribution of AEO content deployment stages</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {data.deploymentStatus && Object.keys(data.deploymentStatus).length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={Object.entries(data.deploymentStatus).map(([name, value]) => ({ name, value }))}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ name, value }: { name: string; value: number }) => `${name}: ${value}`}
                          >
                            {Object.keys(data.deploymentStatus).map((_, i) => (
                              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-muted-foreground text-sm">No deployment data available.</p>
                    )}
                  </CardContent>
                </Card>

                {/* AI Visibility & Coverage */}
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Bot className="h-5 w-5" />
                        AI Visibility
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{data.aiVisibility.message}</AlertDescription>
                      </Alert>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Content Coverage</CardTitle>
                      <CardDescription>
                        {formatNumber(data.summary.totalPublished)} of {formatNumber(data.summary.totalCatalog)} catalog products
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Coverage</span>
                          <span className="font-medium">{formatPercentRaw(data.summary.catalogCoverage)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-blue-600 h-3 rounded-full transition-all"
                            style={{ width: `${Math.min(data.summary.catalogCoverage, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Deployment</span>
                          <span className="font-medium">{formatPercentRaw(data.summary.deploymentRate)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-green-600 h-3 rounded-full transition-all"
                            style={{ width: `${Math.min(data.summary.deploymentRate, 100)}%` }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            </> /* end {data && <> */}

            {/* Tab 3: Ads Performance — outside the legacy guard: rendered from its own request's state only */}
            <TabsContent value="ads" className="space-y-6">
              {adsLoading ? <LoadingSkeleton /> : adsError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{adsError}</AlertDescription>
                </Alert>
              ) : !adsData ? null : adsData.source === 'rollup' && adsData.completeness.included === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    The daily Ads summary has no refreshed day in this range yet
                    ({adsData.completeness.days} days: {adsCompletenessText(adsData.completeness)}). Figures appear as days are refreshed.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  {/* Shown whatever the aggregate holds: an empty result over a partial range is still partial. */}
                  {adsData.source === 'rollup' && !adsData.completeness.complete && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Partial data: the figures below include {adsData.completeness.included} of {adsData.completeness.days} days,{' '}
                        {adsData.completeness.ok} of them fully up to date ({adsCompletenessText(adsData.completeness)}).
                      </AlertDescription>
                    </Alert>
                  )}
                  {adsAeo ? (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <ComparisonCard
                      title="Conversion Rate"
                      aeoValue={adsAeo.conversionRate}
                      nonAeoValue={adsNonAeo?.conversionRate ?? 0}
                      format="percent"
                    />
                    <ComparisonCard
                      title="Avg CPC"
                      aeoValue={adsAeo.avgCpc}
                      nonAeoValue={adsNonAeo?.avgCpc ?? 0}
                      format="currency"
                      inverted
                    />
                    <ComparisonCard
                      title="Total Conversions"
                      aeoValue={adsAeo.totalConversions}
                      nonAeoValue={adsNonAeo?.totalConversions ?? 0}
                    />
                  </div>

                  {/* Quality Score Distribution */}
                  {adsData.qualityDistribution && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Landing Page Quality Distribution</CardTitle>
                        <CardDescription>Google Ads landing page experience scores</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-6">
                          {['aeo', 'non_aeo'].map(grp => {
                            const dist = adsData.qualityDistribution[grp] || {};
                            const chartData = Object.entries(dist).map(([name, value]) => ({ name, value }));
                            if (chartData.length === 0) return null;
                            return (
                              <div key={grp}>
                                <p className="text-sm font-medium mb-2">{grp === 'aeo' ? 'AEO Pages' : 'Non-AEO Pages'}</p>
                                <ResponsiveContainer width="100%" height={200}>
                                  <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill={grp === 'aeo' ? '#3b82f6' : '#94a3b8'} />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
                  ) : (
                    <Alert><AlertDescription>No Google Ads data available for product pages in this period.</AlertDescription></Alert>
                  )}
                </>
              )}
            </TabsContent>

            {/* Por producto / 3A tabs — only rendered when availability confirmed or call failed */}
            {(productAvailable === true || availabilityError !== null) && (
              <TabsContent value="por-producto" className="space-y-4">
                {availabilityError !== null ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{availabilityError}</AlertDescription>
                  </Alert>
                ) : (
                  <ImpactProductTab
                    siteId={siteId}
                    dateFrom={productDateFrom}
                    dateTo={effectiveProductDateTo}
                    preset={productPreset}
                    onPresetChange={setProductPreset}
                    customFrom={productCustomFrom}
                    customTo={productCustomTo}
                    onCustomChange={(f, t) => { setProductCustomFrom(f); setProductCustomTo(t); }}
                  />
                )}
              </TabsContent>
            )}

            {(productAvailable === true || availabilityError !== null) && (
              <TabsContent value="informe-diario" className="space-y-4">
                {availabilityError !== null ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{availabilityError}</AlertDescription>
                  </Alert>
                ) : (
                  <>
                    {informeLoading && <LoadingSkeleton />}
                    {informeError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{informeError}</AlertDescription>
                      </Alert>
                    )}
                    {!informeLoading && !informeError && !informeData && (
                      <Alert>
                        <AlertDescription>
                          Click &ldquo;Impact and attribution&rdquo; to load the attribution data.
                        </AlertDescription>
                      </Alert>
                    )}
                    {informeData && <InformeDiarioTab data={informeData} />}
                  </>
                )}
              </TabsContent>
            )}

            {(productAvailable === true || availabilityError !== null) && (
              <TabsContent value="impact-did" className="space-y-4">
                {availabilityError !== null ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{availabilityError}</AlertDescription>
                  </Alert>
                ) : (
                  <>
                    {didLoading && <LoadingSkeleton />}
                    {didError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{didError}</AlertDescription>
                      </Alert>
                    )}
                    {!didLoading && !didError && !didData && (
                      <Alert>
                        <AlertDescription>
                          Click &ldquo;Impact: DiD (difference in differences)&rdquo; to load the data.
                        </AlertDescription>
                      </Alert>
                    )}
                    {didData && <ImpactDidTab data={didData} />}
                  </>
                )}
              </TabsContent>
            )}

          </Tabs>
        </>
    </div>
  );
}
